import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RefreshActions } from '../../state/refresh'
import { useRequests } from '../../requests/useRequests'
import { isMinimumPackage } from '../../utils/package-validator'
import { Package } from '../../enums/package.enum'
import { State } from '../../types/state'
import { TaskBoardSummary } from '../../types/task-board'
import { SideMenu } from '../Header/SiteMenu'
import AppNavBar from '../Header/AppNavBar'

const TaskBoardsList: React.FC = () => {
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const { package: userPackage, packageExpiredAt, id: userId } = useSelector((state: State) => state.user)

  // Stan aplikacji
  const [boards, setBoards] = useState<TaskBoardSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [showEditBoardModal, setShowEditBoardModal] = useState(false)
  const [editingBoard, setEditingBoard] = useState<TaskBoardSummary | null>(null)
  const [showNewBoardModal, setShowNewBoardModal] = useState(false)
  const [newBoardData, setNewBoardData] = useState({ title: '', description: '' })

  useEffect(() => {
    fetchBoards()
  }, [])

  const fetchBoards = async () => {
    try {
      // Pobieranie rzeczywistych danych z API
      const response = await requestMaker.getTaskBoards()
      setBoards(response.data)
      setLoading(false)
    } catch (error) {
      console.error('Błąd podczas pobierania tablic zadań', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się pobrać tablic zadań'
        })
      )
      setLoading(false)
    }
  }

  const handleEditBoard = async () => {
    if ((editingBoard == null) || !editingBoard.title.trim()) return

    try {
      const response = await requestMaker.updateTaskBoard(editingBoard.id, {
        title: editingBoard.title,
        description: editingBoard.description
      })

      setBoards(boards.map(board =>
        board.id === editingBoard.id ? { ...board, ...response.data } : board
      ))

      setEditingBoard(null)
      setShowEditBoardModal(false)

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Tablica zadań została zaktualizowana'
        })
      )
    } catch (error) {
      console.error('Błąd podczas aktualizacji tablicy zadań', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zaktualizować tablicy zadań'
        })
      )
    }
  }

  const handleDeleteBoard = async (boardId: string, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (!window.confirm('Czy na pewno chcesz usunąć tę tablicę zadań? Ta operacja jest nieodwracalna.')) {
      return
    }

    try {
      await requestMaker.deleteTaskBoard(boardId)

      // Usuń tablicę z lokalnego stanu
      setBoards(boards.filter(board => board.id !== boardId))

      dispatch(
        RefreshActions.setToastMessage({
          message: 'Tablica zadań została usunięta'
        })
      )
    } catch (error) {
      console.error('Błąd podczas usuwania tablicy zadań', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć tablicy zadań'
        })
      )
    }
  }

  const handleEditClick = (board: TaskBoardSummary, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setEditingBoard(board)
    setShowEditBoardModal(true)
  }

  const handleCreateBoard = async () => {
    if (!newBoardData.title.trim()) return
    try {
      const response = await requestMaker.createTaskBoard(newBoardData)
      setBoards(prev => [...prev, response.data])
      setNewBoardData({ title: '', description: '' })
      setShowNewBoardModal(false)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Tablica zadań została utworzona'
        })
      )
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się utworzyć tablicy zadań'
        })
      )
    }
  }

  const generateColorFromUsername = (username: string): string => {
    let hash = 0
    for (let i = 0; i < username.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash)
    }

    // Generuj kolor w formacie HSL dla lepszej czytelności
    const hue = hash % 360
    return `hsl(${hue}, 70%, 60%)`
  }

  if (loading) {
    return (
      <div className='loading-container'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
        <p className='mt-3'>Ładowanie tablic zadań...</p>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        rightIcons={[]}
      />
      <div className='d-flex' style={{ minHeight: 'calc(100vh)' }}>
        <SideMenu />
        <section className='task-boards-list pt-40 pb-40' style={{ flex: 1, overflow: 'auto', padding: '0 24px', backgroundColor: '#f8f9fa' }}>
          <div className='container' style={{ maxWidth: '1400px', margin: '0 auto' }}>
            {/* Przycisk Utwórz Tablicę - teraz wyśrodkowany nad tablicami */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '32px 0 24px 0' }}>
              <button
                onClick={() => setShowNewBoardModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: 'none',
                  padding: 0
                }}
                onMouseEnter={e => {
                  const circle = e.currentTarget.querySelector('.circle') as HTMLElement | null;
                  const text = e.currentTarget.querySelector('.text') as HTMLElement | null;
                  if (circle) {
                    circle.style.borderColor = '#47a145';
                    circle.style.color = '#47a145';
                  }
                  if (text) {
                    text.style.color = '#47a145';
                  }
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
                onMouseLeave={e => {
                  const circle = e.currentTarget.querySelector('.circle') as HTMLElement | null;
                  const text = e.currentTarget.querySelector('.text') as HTMLElement | null;
                  if (circle) {
                    circle.style.borderColor = '#b0b0b0';
                    circle.style.color = '#b0b0b0';
                  }
                  if (text) {
                    text.style.color = '#b0b0b0';
                  }
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <span
                  className="circle"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 64,
                    height: 64,
                    border: '4px solid #b0b0b0',
                    borderRadius: '50%',
                    color: '#b0b0b0',
                    fontSize: 40,
                    fontWeight: 400,
                    marginRight: 16,
                    transition: 'all 0.2s'
                  }}
                >
                  +
                </span>
                <span
                  className="text"
                  style={{
                    color: '#b0b0b0',
                    fontSize: 28,
                    fontWeight: 400,
                    textAlign: 'left',
                    lineHeight: 1.1,
                    transition: 'all 0.2s'
                  }}
                >
                  Utwórz<br />Tablicę
                </span>
              </button>
            </div>
            <div className='row'>
              {boards.map(board => (
                <div key={board.id} className='col-sm-12 col-md-6 col-lg-4 mb-4'>
                  <div className='task-board-card' style={{ position: 'relative' }}>
                    {/* Przyciski edycji i usuwania w prawym górnym rogu */}
                    {isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt) && board.user?.id?.toString() === userId?.toString() && (
                      <div className='task-board-actions'>
                        <button
                          onClick={(e) => handleEditClick(board, e)}
                          className='task-board-action-btn edit'
                          title='Edytuj tablicę'
                        >
                          <i className='fa fa-edit' />
                        </button>
                        <button
                          onClick={async (e) => await handleDeleteBoard(board.id, e)}
                          className='task-board-action-btn delete'
                          title='Usuń tablicę'
                        >
                          <i className='fa fa-trash' />
                        </button>
                      </div>
                    )}
                    <Link
                      to={`/tasks/${board.id}`}
                      style={{ display: 'block', height: '100%' }}
                    >
                      <div className='card-body d-flex flex-column'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <h3 className='card-title mb-0'>{board.title}</h3>
                        </div>
                        <p className='card-text flex-grow-1'>
                          {board.description.length > 120
                            ? `${board.description.substring(0, 120)}...`
                            : board.description}
                        </p>
                        <div className='task-board-meta'>
                          <div className='meta-item mt-3'>
                            <div className='meta-label mb-2'>Członkowie</div>
                            <div className='d-flex align-items-center'>
                              {(board.members || []).slice(0, 4).map((member, index) => (
                                <div
                                      key={member.id}
                                      className='member-avatar'
                                      title={member.username}
                                      style={{
                                        zIndex: 5 - index,
                                        backgroundColor: generateColorFromUsername(member.username)
                                      }}
                                    >
                                      {member.username.charAt(0).toUpperCase()}
                                    </div>
                              ))}
                              {(board.members || []).length > 4 && (
                                <div
                                      className='member-avatar'
                                      style={{
                                        backgroundColor: '#6c757d',
                                        zIndex: 0
                                      }}
                                    >
                                      +{(board.members || []).length - 4}
                                    </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className='mt-3 pt-3 border-top'>
                          <small className='text-muted'>
                            Ostatnia aktualizacja: {new Date(board.updatedAt).toLocaleDateString('pl-PL')}
                          </small>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Modal do edycji tablicy */}
      {showEditBoardModal && (editingBoard != null) && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Edytuj tablicę zadań</h5>
                <button type='button' className='close' onClick={() => setShowEditBoardModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                <div className='form-group'>
                  <label>Nazwa tablicy</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Wprowadź nazwę tablicy'
                    value={editingBoard.title}
                    onChange={(e) => setEditingBoard({ ...editingBoard, title: e.target.value })}
                  />
                </div>
                <div className='form-group'>
                  <label>Opis tablicy</label>
                  <textarea
                    className='form-control'
                    rows={4}
                    placeholder='Wprowadź opis tablicy'
                    value={editingBoard.description}
                    onChange={(e) => setEditingBoard({ ...editingBoard, description: e.target.value })}
                  />
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className={`
                                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                  onClick={() => setShowEditBoardModal(false)}
                >
                  Anuluj
                </button>

                <button
                  type='button'
                  className={`
                                      tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
                  onClick={handleEditBoard}
                  disabled={!editingBoard.title.trim()}
                >
                  Zapisz zmiany
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal do tworzenia tablicy */}
      {showNewBoardModal && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Utwórz nową tablicę zadań</h5>
                <button type='button' className='close' onClick={() => setShowNewBoardModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                <div className='form-group'>
                  <label>Nazwa tablicy</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Wprowadź nazwę tablicy'
                    value={newBoardData.title}
                    onChange={(e) => setNewBoardData({ ...newBoardData, title: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateBoard();
                      }
                    }}
                  />
                </div>
                <div className='form-group'>
                  <label>Opis tablicy</label>
                  <textarea
                    className='form-control'
                    rows={4}
                    placeholder='Wprowadź opis tablicy'
                    value={newBoardData.description}
                    onChange={(e) => setNewBoardData({ ...newBoardData, description: e.target.value })}
                  />
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className={`
                   tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                   tw-text-white
                   tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                 `}
                 onClick={() => setShowNewBoardModal(false)}
               >
                 Anuluj
               </button>
               <button
                 type='button'
                 className={`
                   tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                   tw-text-white
                   tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                 `}
                 onClick={handleCreateBoard}
                 disabled={!newBoardData.title.trim()}
               >
                 Utwórz tablicę
               </button>
             </div>
           </div>
         </div>
       </div>
     )}
    </>
  )
}

export default TaskBoardsList
